import { useRef, type ChangeEvent } from "react";
import { mediaUrl } from "@/lib/media";

export type ImageTarget = "photo" | "card1" | "card2";

interface ImageEditorProps {
  photo: string;
  card1: string;
  card2: string;
  onChange: (target: ImageTarget, file: File) => void;
  onRemove: (target: ImageTarget) => void;
  busy?: boolean;
}

const pickBtn =
  "whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100 disabled:opacity-50";
const delBtn =
  "whitespace-nowrap rounded-md border border-accent-200 bg-accent-50 px-3 py-1.5 text-xs font-semibold text-accent-700 transition-colors hover:bg-accent-100 disabled:opacity-50";

// 회원 사진 + 명함 앞/뒤 이미지 편집
export default function ImageEditor({
  photo,
  card1,
  card2,
  onChange,
  onRemove,
  busy = false,
}: ImageEditorProps) {
  const photoRef = useRef<HTMLInputElement>(null);
  const card1Ref = useRef<HTMLInputElement>(null);
  const card2Ref = useRef<HTMLInputElement>(null);

  const pick = (target: ImageTarget) => (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file) onChange(target, file);
    e.target.value = "";
  };

  const cards: { target: ImageTarget; url: string; label: string; ref: typeof card1Ref }[] = [
    { target: "card1", url: card1, label: "앞면", ref: card1Ref },
    { target: "card2", url: card2, label: "뒷면", ref: card2Ref },
  ];

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-base font-bold text-foreground-900">사진</h2>
        <span className="text-xs text-foreground-400">jpg / png / webp, 20MB 이하</span>
      </div>

      {/* 회원 사진 */}
      <div className="mt-4 flex items-center gap-4">
        <span className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border border-background-200 bg-background-100">
          {photo ? (
            <img
              src={mediaUrl(photo)}
              alt="회원 사진 미리보기"
              className="h-full w-full object-cover object-top"
            />
          ) : (
            <i className="ri-user-line text-2xl text-foreground-400" aria-hidden="true"></i>
          )}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-800">회원 사진</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => photoRef.current?.click()}
              className={pickBtn}
            >
              사진 변경
            </button>
            {photo && (
              <button
                type="button"
                disabled={busy}
                onClick={() => onRemove("photo")}
                className={delBtn}
              >
                삭제
              </button>
            )}
          </div>
        </div>
        <input
          ref={photoRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="sr-only"
          onChange={pick("photo")}
        />
      </div>

      {/* 명함 앞/뒤 */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {cards.map(({ target, url, label, ref }) => (
          <div key={target}>
            <p className="mb-2 text-sm font-semibold text-foreground-800">
              명함 {label}
            </p>
            <div className="relative flex aspect-[9/5] w-full items-center justify-center overflow-hidden rounded-lg border border-background-200 bg-background-100">
              {url ? (
                <img
                  src={mediaUrl(url)}
                  alt={`명함 ${label} 미리보기`}
                  className="h-full w-full object-contain"
                />
              ) : (
                <span className="text-xs text-foreground-400">이미지 없음</span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => ref.current?.click()}
                className={pickBtn}
              >
                변경
              </button>
              {url && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onRemove(target)}
                  className={delBtn}
                >
                  삭제
                </button>
              )}
            </div>
            <input
              ref={ref}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="sr-only"
              onChange={pick(target)}
            />
          </div>
        ))}
      </div>
    </section>
  );
}